/* global GM_STATE, GM_STATE_STOCK, version_two_size_data */
import { parseJSON } from '../../helpers'
import Icon from '@/components/snippets/icon/icon'
import { Badge } from '@/components/snippets/badge/badge-render'
import { PromoLine } from '@/components/snippets/promo-line/render-promo-line'
import {
  checkHeader,
  DEFAULT_WIDTH_IF_PRESENT,
  getAgeGroupLabel,
  getSizeFromTitle,
  resolveSizeAgeGroup,
  handleDisplayStockOption,
  handleGetTypeValue,
  handleHasDefaultValue,
  normaliseAgeGroup,
  sizeGroups,
  sizeTypes,
  sortAgeGroupCategories
} from 'snippets/product/product-selector-utils'
import clsx from 'clsx'
import { render } from 'preact'
import { useEffect, useMemo, useState } from 'preact/hooks'
import { getAthosVariantRecords, getFeedSizes, getProductParentId } from '../helpers/normalisers'
import { sizeRanges } from '../helpers/universalSizeMapping'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'
import { Price, formatPrice } from './Price'
import { KlaviyoStarRating } from './KlaviyoStarRating'
import { generateSrcSet, IMAGE_WIDTHS } from './ResultImage'
import { getVariantOptionLabel, useVariantTextOverrides } from '@/components/snippets/variant-text-overrides/use-variant-text-overrides'
import { useBeaconAnalytics } from '../hooks/useBeaconAnalytics'

const MODAL_ID = 'modal-quick-add'

const QUICK_ADD_SIZE_TYPES = sizeTypes.map((type) => ({
  ...type,
  label: type.value === 'UK' ? 'AU/UK' : type.label
}))

const UK_META_KEYS = ['kids_uk', 'size_uk', 'women_uk', 'womens_uk', 'men_uk', 'mens_uk']
const US_META_KEYS = ['kids_us', 'size_us', 'women_us', 'womens_us', 'men_us', 'mens_us']
const EU_META_KEYS = ['euro']
const CM_META_KEYS = ['centimeter']
const AGE_META_KEYS = ['age_group']

function asList(value) {
  if (value == null || value === false) return []
  if (Array.isArray(value)) return value.flatMap(asList)
  if (typeof value === 'object') {
    if ('value' in value) return asList(value.value)
    return []
  }
  const text = String(value).trim()
  if (!text || text === '[object Object]') return []
  if (text.startsWith('[') && text.endsWith(']')) {
    const parsed = parseJSON(text, 'array')
    if (Array.isArray(parsed) && parsed.length) return parsed.flatMap(asList)
  }
  if (text.includes('|')) return text.split('|').map((part) => part.trim()).filter(Boolean)
  return [text]
}

function asScalar(value) {
  const list = asList(value)
  return list.length === 1 ? list[0] : ''
}

function getRawField(source, key) {
  if (!source) return null
  return (
    source[`mfield_utopia_${key}`] ??
    source[`metafield_utopia_${key}`] ??
    source[`utopia_${key}`] ??
    source[key] ??
    null
  )
}

function pickMeta(variant, result, keys, index, variantCount) {
  for (const key of keys) {
    const raw = getRawField(variant, key)
    const scalar = asScalar(raw)
    if (scalar) return scalar
    const aligned = asList(raw)
    if (aligned.length === variantCount && aligned[index]) return String(aligned[index]).trim()
  }

  for (const key of keys) {
    const raw = getRawField(result, key)
    const aligned = asList(raw)
    if (aligned.length === variantCount && aligned[index]) return String(aligned[index]).trim()
  }

  if (Array.isArray(variant?.metafields)) {
    for (const key of keys) {
      const match = variant.metafields.find((m) => (m.namespace === 'utopia' || !m.namespace) && m.key === key)
      const scalar = asScalar(match?.value)
      if (scalar) return scalar
    }
  }

  return ''
}

function getOptionValue(variant, matchers) {
  if (!variant) return ''
  const options = variant.options
  if (options && typeof options === 'object' && !Array.isArray(options)) {
    for (const [key, value] of Object.entries(options)) {
      if (matchers.some((matcher) => key.toLowerCase().includes(matcher))) {
        const resolved = value?.value ?? value
        const scalar = asScalar(resolved)
        if (scalar) return scalar
      }
    }
  }
  return ''
}

function extractWidthCode(value) {
  const text = asScalar(value) || (Array.isArray(value) ? '' : String(value || '').trim())
  if (/^[A-Z]{1,2}$/i.test(text)) return text.toUpperCase()
  const parts = text.split('/')
  if (parts.length >= 2) {
    const widthPart = parts[parts.length - 1].trim().split(/\s/)[0]
    if (/^[A-Z]{1,2}$/i.test(widthPart)) return widthPart.toUpperCase()
  }
  return ''
}

function toSelectorOption(variant, result, index, variantCount) {
  const id = variant.id ?? variant.uid ?? variant.variant_id ?? variant.sku
  if (!id) return null

  const name = variant.title || variant.name || getOptionValue(variant, ['title']) || ''
  const uk =
    pickMeta(variant, result, UK_META_KEYS, index, variantCount) ||
    getOptionValue(variant, ['size', 'uk']) ||
    asScalar(variant.option1)
  const us = pickMeta(variant, result, US_META_KEYS, index, variantCount)
  const eu = pickMeta(variant, result, EU_META_KEYS, index, variantCount)
  const cm = pickMeta(variant, result, CM_META_KEYS, index, variantCount)
  const width =
    extractWidthCode(variant.option2) ||
    extractWidthCode(getOptionValue(variant, ['width'])) ||
    extractWidthCode(name)
  const ageGroup = pickMeta(variant, result, AGE_META_KEYS, index, variantCount)

  return {
    id,
    sku: variant.sku || '',
    price: variant.price,
    name,
    available: isVariantInStock(variant),
    age_group: resolveSizeAgeGroup({ ageGroup, eu, label: uk || name }),
    cm,
    uk,
    us,
    eu,
    width
  }
}

function parseAthosVariants(result) {
  const variants = getAthosVariantRecords(result)
  const mapped = variants.map((variant, index) => toSelectorOption(variant, result, index, variants.length)).filter(Boolean)

  const feedSizes = getFeedSizes(result)
  if (Array.isArray(feedSizes) && feedSizes.length > mapped.length) {
    return feedSizes.map((size) => {
      const label = size.label || size.uk || size.size || ''
      return {
        id: size.variant_id || size.id,
        name: label,
        available: size.available === undefined ? true : Boolean(Number(size.available)),
        age_group: resolveSizeAgeGroup({ ageGroup: size.age_group, eu: size.eu || label, label }),
        cm: size.cm || '',
        uk: size.uk || label,
        us: size.us || '',
        eu: size.eu || '',
        width: size.width || ''
      }
    }).filter((option) => option.id)
  }

  return mapped
}

function getProductHandle(result) {
  if (result?.handle) return result.handle
  const url = result?.url || result?.link || ''
  const match = String(url).match(/\/products\/([^/?#]+)/)
  return match ? decodeURIComponent(match[1]) : ''
}

function getProductUrl(result) {
  if (result?.url) return result.url
  if (result?.link) return result.link
  const handle = getProductHandle(result)
  return handle ? `/products/${handle}` : ''
}

const parentSizeCache = new Map()

function mapQuickAddVariants(variants) {
  if (!Array.isArray(variants)) return []
  return variants.map((option) => ({
    id: option.id,
    sku: option.sku || '',
    price: option.price,
    name: option.name || '',
    available: option.available !== false,
    age_group: normaliseAgeGroup(option.age_group) || 'Sizes',
    cm: option.cm || '',
    uk: option.uk || option.option1 || '',
    us: option.us || '',
    eu: option.eu || '',
    width: option.width || '',
    option1: option.option1 || ''
  }))
}

function getResultOptionNames(result) {
  const names = result?.options || result?.option_names
  if (Array.isArray(names)) {
    return names.map((option) => (typeof option === 'string' ? option : option?.name)).filter(Boolean)
  }
  return []
}

function normaliseQuickAddPayload(data) {
  if (Array.isArray(data)) {
    return { options: mapQuickAddVariants(data), small: '', bigger: '', optionNames: [] }
  }
  return {
    options: mapQuickAddVariants(data?.variants),
    small: data?.small || '',
    bigger: data?.bigger || '',
    optionNames: Array.isArray(data?.option_names) ? data.option_names : []
  }
}

async function fetchParentSizeOptions(result) {
  const handle = getProductHandle(result)
  if (!handle) return { options: [], small: '', bigger: '', optionNames: getResultOptionNames(result) }
  if (parentSizeCache.has(handle)) {
    const cached = await parentSizeCache.get(handle)
    return cached?.options ? cached : normaliseQuickAddPayload(cached)
  }

  const request = (async () => {
    const response = await fetch(`/products/${handle}?view=quick-add`)
    if (!response.ok) throw new Error('Failed to load parent sizes')
    return normaliseQuickAddPayload(await response.json())
  })()

  parentSizeCache.set(handle, request)
  try {
    const payload = await request
    parentSizeCache.set(handle, payload)
    return payload
  } catch (error) {
    parentSizeCache.delete(handle)
    console.error('Error loading quick add sizes:', error)
    return { options: [], small: '', bigger: '', optionNames: getResultOptionNames(result) }
  }
}

function isVariantInStock(option) {
  if (!option) return false
  if (option.available === false || option.available === 'false') return false
  if (typeof option.inventory_quantity === 'number') return option.inventory_quantity > 0
  return option.available !== false
}

function normalizeSizeType(value) {
  if (value === 'EU' || value === 'EURO') return 'EURO'
  if (value === 'AU' || value === 'AU/UK') return 'UK'
  if (value === 'US' || value === 'UK' || value === 'CM') return value
  return 'UK'
}

function parseVariantId(variant) {
  if (!variant?.id) return null
  return typeof variant.id === 'string' ? parseInt(variant.id, 10) : variant.id
}

function closeQuickAddModal() {
  const modal = document.querySelector(`x-modal[data-id="${MODAL_ID}"]`)
  if (modal && typeof modal.handleHide === 'function') {
    modal.handleHide()
  }
}

function openQuickAddModal() {
  const modal = document.querySelector(`x-modal[data-id="${MODAL_ID}"]`)
  if (modal && typeof modal.handleShow === 'function') {
    modal.handleShow()
  }
}

async function addVariantToCart(variant) {
  const variantId = parseVariantId(variant)
  if (!variantId || !window.Cart) return false

  await window.Cart.addLineItems({
    id: variantId,
    quantity: 1
  })
  return true
}

function getStockMessage(option, rawVariants) {
  const raw = rawVariants.find((variant) => String(variant.id) === String(option?.id))
  const quantity = typeof raw?.inventory_quantity === 'number' ? raw.inventory_quantity : null
  if (quantity != null && quantity <= 0) return 'Sold Out'
  if (option && !option.available) return 'Sold Out'

  const stockLevels = GM_STATE_STOCK?.stock_levels
  if (!stockLevels?.length || quantity == null) return ''

  const sorted = [...stockLevels].sort((a, b) => a.stockLevel - b.stockLevel)
  const match = sorted.find((level) => quantity <= level.stockLevel)
  return match?.message ?? ''
}

function TabSwitcher({ activeTab, onChange }) {
  const tabs = [
    { id: 'size', label: 'Select Size' },
    {
      id: 'guide',
      label: (
      <>
        <span className="hidden md:inline mr-1">View </span>
        Size Guide
      </>
      )
    }
  ]

  return (
    <div className="shrink-0 flex items-stretch rounded-4xl bg-primary-white p-1">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={clsx(
            'shrink-0 btn btn-md btn-inline !text-sm px-4 md:px-6 !gap-0',
            activeTab === tab.id ? 'btn-solid-black' : 'btn-solid-white'
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

function UniversalSizeTabs({ sizeType, onChange, validHeaders }) {
  const availableTypes = QUICK_ADD_SIZE_TYPES.filter((type) =>
    validHeaders.includes(type.label === 'AU/UK' ? 'uk' : type.label.toLowerCase())
  )
  const types = availableTypes.length ? availableTypes : QUICK_ADD_SIZE_TYPES

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 text-right">
      {types.map((type, index) => (
        <button
          type="button"
          onClick={() => onChange(type.value)}
          className={clsx(
            'paragraph-sm font-medium underline transition-colors',
            sizeType === type.value ? 'text-primary-black' : 'text-neutrals-secondary-grey'
          )}
        >
          {type.label}
        </button>
      ))}
    </div>
  )
}

function getChipValue(sizeType, option) {
  const typed = handleGetTypeValue(sizeType, option)
  if (typed) return typed
  if (!option?.name) return ''
  const fromTitle = getSizeFromTitle(option, sizeType)
  if (fromTitle && fromTitle !== '-') return fromTitle
  return ''
}

function SizeChip({ option, selected, sizeType, onSelect, overrides, optionNames }) {
  const value = getChipValue(sizeType, option)
  if (!value) return null

  const isSelected = String(selected.id) === String(option.id)
  const inStock = option.available !== false
  const label = getVariantOptionLabel(overrides, value, optionNames)

  return (
    <button
      type="button"
      onClick={() => onSelect(option)}
      aria-pressed={isSelected}
      className={clsx(
        'flex relative w-[calc((100%-(0.5rem*6))/7)] h-[44px] items-center justify-center paragraph-sm text-center px-1 border border-neutrals-medium-grey rounded-md overflow-hidden transition-all',
        isSelected && inStock && 'text-primary-white bg-primary-blue border-primary-blue',
        isSelected && !inStock && 'text-primary-white bg-neutrals-medium-grey border-neutrals-medium-grey',
        !isSelected && inStock && 'text-primary-black bg-primary-white border-neutrals-medium-grey',
        !isSelected && !inStock && 'text-neutrals-tertiary-grey bg-primary-white border-neutrals-medium-grey'
      )}
    >
      <span>{label}</span>
      {!inStock && <span className={clsx('absolute w-px h-[150%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-[40deg] pointer-events-none z-2 transition-colors', isSelected ? 'bg-primary-white' : 'bg-neutrals-medium-grey')}></span>}
    </button>
  )
}

function WidthChips({ widths, selectedWidth, onSelect, overrides, optionNames }) {
  if (!widths?.length || widths.length < 2) return null

  return (
    <div className="flex flex-col gap-3">
      <p className="paragraph-sm font-bold">Select Width:</p>
      <div className="flex flex-row flex-wrap gap-2">
        {widths.map((widthOption) => {
          const code = widthOption.width
          const isSelected = selectedWidth === code
          const inStock = widthOption.hasStock !== false
          return (
            <button
              key={code}
              type="button"
              data-width={code}
              disabled={!inStock}
              onClick={() => inStock && onSelect(code)}
              className={clsx(
                'flex w-[calc((100%-(0.5rem*2))/3)] h-[44px] items-center justify-center paragraph-sm text-center px-1 border border-neutrals-medium-grey rounded-md overflow-hidden transition-all',
                isSelected && inStock && 'text-primary-white bg-primary-blue border-primary-blue',
                isSelected && !inStock && 'text-primary-white bg-neutrals-medium-grey border-neutrals-medium-grey',
                !isSelected && inStock && 'text-primary-black bg-primary-white border-neutrals-medium-grey',
                !isSelected && !inStock && 'text-neutrals-tertiary-grey bg-primary-white border-neutrals-medium-grey'
              )}
            >
              <span>{getVariantOptionLabel(overrides, code, optionNames)}</span>
              {!inStock && <span className={clsx('absolute w-px h-[150%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-[40deg] pointer-events-none z-2 transition-colors', isSelected ? 'bg-primary-white' : 'bg-neutrals-medium-grey')}></span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function getDisplayOptions(ageGroup, width) {
  if (!ageGroup) return []
  if (Array.isArray(ageGroup)) return ageGroup
  if (width && Array.isArray(ageGroup[width])) return ageGroup[width]
  return Object.values(ageGroup)
    .flat()
    .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
}

function LinkedSizeChip({ label, href }) {
  if (!href) return null
  return (
    <a
      href={href}
      className="flex w-[calc(((100%-(0.5rem*6))/7*2)+0.5rem)] h-[44px] items-center justify-center paragraph-sm text-center bg-primary-white px-1 border border-neutrals-medium-grey rounded-md overflow-hidden transition-all"
      aria-label={label}
    >
      {label}
    </a>
  )
}

function SizeSelector({ options, selected, onSelect, sizeType, productWidths, linkedOptions, result, optionNames }) {
  const groups = useMemo(() => sizeGroups(options), [options])
  const categories = useMemo(() => sortAgeGroupCategories(Object.keys(groups)), [groups])
  const criteria = useMemo(
    () => ({
      tags: result?.tags ?? [],
      productIds: [result?.parentId, result?.id, result?.uid].filter(Boolean),
      collectionIds: result?.collection_id
    }),
    [result]
  )
  const overrides = useVariantTextOverrides(criteria)

  const activeWidth = selected.width || selected.tmpWidth || productWidths.width
  const visibleCategories = categories.filter((category) => getDisplayOptions(groups[category], activeWidth).length)

  return (
    <div className="flex flex-col gap-4">
      {visibleCategories.map((category, index) => {
        const displayOptions = getDisplayOptions(groups[category], activeWidth)

        return (
          <div key={category} className="flex flex-col gap-3 pb-4 border-b last:border-b-0 border-neutrals-medium-grey">
            <p className="paragraph-xs font-bold">{getAgeGroupLabel(category)}</p>
            <div className="flex flex-row flex-wrap gap-2">
              {index === 0 && (
                <LinkedSizeChip label="View Younger Sizes" href={linkedOptions?.small ? `/products/${linkedOptions.small}` : ''} />
              )}
              {displayOptions.map((option) => (
                <SizeChip
                  key={option.id}
                  option={option}
                  selected={selected}
                  sizeType={sizeType}
                  onSelect={onSelect}
                  overrides={overrides}
                  optionNames={optionNames}
                />
              ))}
              {index === visibleCategories.length - 1 && (
                <LinkedSizeChip label="View Older Sizes" href={linkedOptions?.bigger ? `/products/${linkedOptions.bigger}` : ''} />
              )}
            </div>
          </div>
        )
      })}
      <WidthChips
        widths={productWidths.widthOptions}
        selectedWidth={activeWidth}
        overrides={overrides}
        optionNames={optionNames}
        onSelect={(width) => {
          const inWidth = options.filter((option) => option.width === width)
          const currentValue = selected.id ? handleGetTypeValue(sizeType, selected) : null
          const match = inWidth.find((option) => handleGetTypeValue(sizeType, option) === currentValue)
          if (match) {
            onSelect({ ...match, width })
            return
          }
          onSelect({ id: '', name: '', width, tmpWidth: width, value: '', available: true })
        }}
      />
    </div>
  )
}

function FallbackSizeGuide() {
  const groups = Object.entries(sizeRanges)
  if (!groups.length) return null

  return (
    <table className="w-full border-collapse text-center">
      <thead>
        <tr>
          {['Age Group', 'AU/UK', 'US', 'EU'].map((heading) => (
            <th key={heading} className="paragraph-sm py-3 font-bold">
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {groups.flatMap(([group, rows]) =>
          rows.map((row, index) => (
            <tr key={`${group}-${index}`} className="border-t border-primary-black">
              <td className="paragraph-sm py-3">{index === 0 ? group : ''}</td>
              <td className="paragraph-sm py-3">{row.UK}</td>
              <td className="paragraph-sm py-3">{row.US}</td>
              <td className="paragraph-sm py-3">{row.EU}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}

function getQuickAddImage(result) {
  return result.image || result.images?.[0]?.url || result.images?.[0] || ''
}

function QuickAddWishlist({ result, variantId, options }) {
  const parentId = getProductParentId(result) || result.id
  const fallbackId = options?.[0]?.id || getAthosVariantRecords(result)[0]?.id
  const id = variantId || fallbackId
  if (!parentId || !id) return null

  const productKey = `${parentId}:${id}`

  if (GM_STATE?.customer?.logged) {
    const image = getQuickAddImage(result)
    const variants = (options || []).map((option) => ({
      id: String(option.id),
      sku: option.sku,
      name: option.name,
      available: option.available,
      image,
      age_group: option.age_group,
      cm: option.cm,
      uk: option.uk,
      us: option.us,
      eu: option.eu,
      width: option.width
    }))

    return (
      <x-omneo-wishlist
        key={productKey}
        data-variants={JSON.stringify(variants)}
        data-product={String(parentId)}
        data-selected-variant={String(id)}
      />
    )
  }

  return (
    <x-storage key={productKey} data-product={productKey} className="group/wishlist">
      <button
        type="button"
        data-role="toggle"
        aria-label="Add to Wishlist"
        className="rounded-full wkh-button wk-headless-wishlist-button !size-[44px] md:!size-[56px]"
      >
        <div className="wkh-icon" icon="wishlist">
          <svg viewBox="0 0 64 64">
            <path
              className="fill-transparent group-data-visible/wishlist:fill-black"
              d="M32.012,59.616c-1.119-.521-2.365-1.141-3.707-1.859a79.264,79.264,0,0,1-11.694-7.614C6.316,42,.266,32.6.254,22.076,0.244,12.358,7.871,4.506,17.232,4.5a16.661,16.661,0,0,1,11.891,4.99l2.837,2.889,2.827-2.9a16.639,16.639,0,0,1,11.874-5.02h0c9.368-.01,17.008,7.815,17.021,17.539,0.015,10.533-6.022,19.96-16.312,28.128a79.314,79.314,0,0,1-11.661,7.63C34.369,58.472,33.127,59.094,32.012,59.616Z"
            />
          </svg>
        </div>
      </button>
    </x-storage>
  )
}

function SizeGuidePanel() {
  const hasData =
    typeof version_two_size_data !== 'undefined' &&
    Array.isArray(version_two_size_data) &&
    version_two_size_data.length > 0

  if (!hasData) return <FallbackSizeGuide />
  return <x-product-size-guide class="block w-full" />
}

function resolveBeaconVariant(result, selected, raw) {
  const athosMatch = getAthosVariantRecords(result).find((variant) => String(variant.id) === String(selected?.id))
  return {
    ...athosMatch,
    ...raw,
    id: selected?.id ?? raw?.id,
    sku: raw?.sku || athosMatch?.sku || result?.sku,
    price: raw?.price ?? athosMatch?.price ?? result?.price
  }
}

function ProductSummary({ result, badges, promoLines, analytics }) {
  const productName = result.mfield_utopia_customer_facing_product_name || result.name || result.title
  const image = result.images?.[0] || (result.image ? { src: result.image, alt: productName } : null)
  const [variant] = getAthosVariantRecords(result)
  const colourCount = (result.swatches || []).filter((swatch) => swatch.published !== false).length
  const productUrl = getProductUrl(result)

  const handleProductClick = () => {
    analytics?.clickthrough(result, { quickView: true })
    closeQuickAddModal()
  }

  return (
    <div className="flex gap-3 md:gap-4 rounded-lg bg-primary-white p-2 md:p-4">
      {image && productUrl ? (
        <a href={productUrl} className="aspect-1 w-[120px] md:w-[130px] shrink-0 overflow-hidden bg-primary-grey" onClick={handleProductClick}>
          <img
            className="h-full w-full object-cover object-bottom"
            src={image.src}
            srcSet={generateSrcSet(image.src, IMAGE_WIDTHS)}
            sizes="130px"
            alt={image.alt || productName || ''}
          />
        </a>
      ) : image ? (
        <div className="aspect-1 w-[120px] md:w-[130px] shrink-0 overflow-hidden bg-primary-grey">
          <img
            className="h-full w-full object-cover object-bottom"
            src={image.src}
            srcSet={generateSrcSet(image.src, IMAGE_WIDTHS)}
            sizes="130px"
            alt={image.alt || productName || ''}
          />
        </div>
      ) : null}
      <div className="grow flex flex-col justify-between gap-2 md:gap-3">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col items-start">
            {badges?.map((badge) => (
              <Badge key={badge.text} badge={badge} badgeLocation="PLP" product={result} size="sm" />
            ))}
            {promoLines?.map((promoLine) => (
              <PromoLine key={promoLine.title} promoline={promoLine} size="sm" />
            ))}
          </div>
          {result.brand && <p className="paragraph-xs font-bold" dangerouslySetInnerHTML={{ __html: result.brand }} />}
          {productName && productUrl ? (
            <a
              href={productUrl}
              className="paragraph-sm"
              onClick={handleProductClick}
              dangerouslySetInnerHTML={{ __html: productName }}
            />
          ) : productName ? (
            <p className="paragraph-sm" dangerouslySetInnerHTML={{ __html: productName }} />
          ) : null}
        </div>

        <KlaviyoStarRating result={result} />

        <hr className="border-neutrals-light-grey" />

        <div className="flex items-end justify-between gap-2">
          <Price price={variant?.price} compareAtPrice={variant?.compare_at_price} />
          {colourCount > 0 && (
            <p className="paragraph-sm text-neutrals-secondary-grey">
              {colourCount === 1 ? '1 Colour' : `${colourCount} Colours`}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function ModalContent({ result, badges, promoLines, analytics }) {
  const parentId = getProductParentId(result)
  const handle = getProductHandle(result)
  const athosOptions = useMemo(() => parseAthosVariants(result), [result])
  const [parentOptions, setParentOptions] = useState(null)
  const [linkedOptions, setLinkedOptions] = useState({ small: '', bigger: '' })
  const [optionNames, setOptionNames] = useState(() => getResultOptionNames(result))
  const [sizesLoading, setSizesLoading] = useState(true)
  const options = parentOptions?.length ? parentOptions : sizesLoading ? [] : athosOptions
  const rawVariants = parentOptions?.length ? parentOptions : getAthosVariantRecords(result)
  const productWidths = useMemo(
    () => handleHasDefaultValue(handleDisplayStockOption(options), DEFAULT_WIDTH_IF_PRESENT),
    [options]
  )
  const defaultSizeRange = result.mfield_utopia_default_size_range || result.metafield_utopia_default_size_range || 'UK'
  const [sizeType, setSizeType] = useState(normalizeSizeType(defaultSizeRange))
  const [activeTab, setActiveTab] = useState('size')
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState({
    id: '',
    name: '',
    width: '',
    tmpWidth: productWidths.width || '',
    available: true
  })

  useEffect(() => {
    let cancelled = false
    setSizesLoading(true)
    fetchParentSizeOptions(result).then((fetched) => {
      if (cancelled) return
      setParentOptions(fetched.options)
      setLinkedOptions({ small: fetched.small, bigger: fetched.bigger })
      setOptionNames(fetched.optionNames?.length ? fetched.optionNames : getResultOptionNames(result))
      setSizesLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [handle, parentId])

  useEffect(() => {
    if (!productWidths.width) return
    setSelected((prev) => {
      if (prev.width || prev.tmpWidth) return prev
      return { ...prev, tmpWidth: productWidths.width }
    })
  }, [productWidths.width])

  const selectedOption = options.find((option) => String(option.id) === String(selected.id))
  const stockMessage = selected.id ? getStockMessage(selectedOption, rawVariants) : ''
  const canAdd = Boolean(selected.id && selectedOption?.available !== false)
  const isSoldOut = Boolean(selected.id && selectedOption && selectedOption.available === false)
  const ctaDisabled = activeTab === 'size' && (sizesLoading || loading || !canAdd)
  const [athosVariant] = getAthosVariantRecords(result)
  const displayPrice = formatPrice(athosVariant?.price ?? result.price)
  const ctaLabel = isSoldOut ? 'Sold Out' : 'Select Size'

  const handleSelect = (option) => {
    setSelected({
      id: option.id || '',
      name: option.name || '',
      width: option.width || selected.width || '',
      tmpWidth: option.width || selected.tmpWidth || productWidths.width || '',
      available: option.available !== false,
      uk: option.uk,
      us: option.us,
      eu: option.eu,
      cm: option.cm
    })
  }

  const handleAddToCart = async () => {
    if (!canAdd) {
      setActiveTab('size')
      return
    }

    const raw = rawVariants.find((variant) => String(variant.id) === String(selected.id))
    setLoading(true)
    try {
      const added = await addVariantToCart(raw || selected)
      if (added) {
        analytics?.addtocart(result, resolveBeaconVariant(result, selected, raw), { qty: 1, quickView: true })
        closeQuickAddModal()
      }
    } catch (error) {
      console.error('Error adding item to cart:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="sticky top-0 z-20 flex shrink-0 items-center justify-between bg-primary-white pl-4 md:pl-6 pr-3 border-b border-neutrals-light-grey">
        <h4 className="h4">Quick Add</h4>
        <button type="button" className="cursor-pointer p-4" aria-label="Close quick add" onClick={closeQuickAddModal}>
          <Icon id="close" className="min-h-6 min-w-6" />
        </button>
      </div>

      <div className="flex min-h-0 grow flex-col gap-4 md:gap-6 overflow-y-auto px-4 md:px-6 py-4 md:py-6">
        <ProductSummary result={result} badges={badges} promoLines={promoLines} analytics={analytics} />
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-x-4 gap-y-2">
            <TabSwitcher activeTab={activeTab} onChange={setActiveTab} />
            {activeTab === 'size' && (
              <UniversalSizeTabs
                sizeType={sizeType}
                onChange={setSizeType}
                validHeaders={options.length ? checkHeader(options) : ['uk', 'us', 'eu', 'cm']}
              />
            )}
          </div>
          {activeTab === 'size' ? (
            sizesLoading ? (
              <div className="relative min-h-40">
                <LoaderSpin wrapperClass="bg-transparent" />
              </div>
            ) : (
              <SizeSelector
                options={options}
                selected={selected}
                onSelect={handleSelect}
                sizeType={sizeType}
                productWidths={productWidths}
                linkedOptions={linkedOptions}
                result={result}
                optionNames={optionNames}
              />
            )
          ) : (
            <SizeGuidePanel />
          )}
          {activeTab === 'size' && stockMessage && stockMessage !== 'Sold Out' && (
            <p className="paragraph-sm text-neutrals-secondary-grey">{stockMessage}</p>
          )}
        </div>
      </div>

      <div className="flex w-full shrink-0 gap-2 bg-primary-white px-4 md:px-6 py-4 md:py-6 border-t border-neutrals-light-grey">
        <button
          className={clsx(
            'grow btn btn-md md:btn-lg !gap-4',
            canAdd ? 'btn-solid-accent' : 'btn-solid-black',
            !isSoldOut && 'disabled:bg-primary-black disabled:text-primary-white disabled:opacity-100'
          )}
          type="button"
          disabled={ctaDisabled}
          onClick={handleAddToCart}
        >
          {canAdd ? (
            <>
              <span>Add To Bag</span>
              {displayPrice && (
                <>
                  <span className="shrink-0 h-4 w-px bg-primary-white" aria-hidden="true" />
                  <span>{displayPrice}</span>
                </>
              )}
            </>
          ) : (
            <span>{ctaLabel}</span>
          )}
        </button>
        <QuickAddWishlist result={result} variantId={selected.id} options={options} />
      </div>
    </>
  )
}

export function ResultQuickAdd({ result, badges, promoLines }) {
  const { analytics } = useBeaconAnalytics()
  const isGiftCard = result?.is_giftcard === 'true' || result?.handle?.includes('gift-card')
  if (isGiftCard) return null

  const handleClick = (event) => {
    event.preventDefault()
    event.stopPropagation()

    const container = document.querySelector('[data-quick-add-inner]')
    if (container) {
      render(
        <ModalContent
          key={result.uid || result.id || result.handle}
          result={result}
          badges={badges}
          promoLines={promoLines}
          analytics={analytics}
        />,
        container
      )
    }
    openQuickAddModal()
  }

  return (
    <button
      type="button"
      className="absolute right-3 top-3 z-10 flex size-8 items-center justify-center rounded-full bg-primary-white text-primary-black md:right-6 md:top-6 md:size-10"
      onClick={handleClick}
      aria-label="Quick add"
    >
      <Icon id="plus" className="h-4 w-4 md:h-5 md:w-5" />
    </button>
  )
}
